/**
 * One hard reload when a Next.js chunk 404s after a deploy.
 *
 * `scripts/deploy.sh` used to `rsync --delete` the live `.next-build` before
 * PM2 restarted, so a browser that already had the previous build asked for a
 * hash the server had just removed. Mobile Safari on the login → /dashboard
 * hop is the case PostHog recorded (issue 01a162cb). A second failure inside
 * a minute is a real missing file, not skew — do not loop.
 */

const STORAGE_KEY = 'delphina.chunkReloadAt';
const WINDOW_MS = 60_000;
const RELOAD_DELAY_MS = 300;

const CHUNK_MESSAGE =
  /Failed to load chunk|Loading chunk [\w./~-]+ failed|Loading CSS chunk/i;

export function isChunkLoadError(error: unknown): boolean {
  if (error == null) return false;
  const name =
    typeof error === 'object' && 'name' in error
      ? String((error as { name?: unknown }).name ?? '')
      : '';
  if (name === 'ChunkLoadError') return true;
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : typeof error === 'object' && 'message' in error
          ? String((error as { message?: unknown }).message ?? '')
          : '';
  return CHUNK_MESSAGE.test(message);
}

function defaultReload(): void {
  window.location.reload();
}

/**
 * Returns true when a reload was scheduled. A non-chunk error, a missing
 * window, or a reload already scheduled in the last minute returns false.
 * The sessionStorage write happens before the timer so a second event in
 * the same tick cannot schedule another reload. If storage throws (private
 * mode), we do not reload — an unguarded reload loops.
 */
export function reloadOnceForChunkError(
  error: unknown,
  reload: () => void = defaultReload,
): boolean {
  if (typeof window === 'undefined') return false;
  if (!isChunkLoadError(error)) return false;
  try {
    const last = Number(window.sessionStorage.getItem(STORAGE_KEY) || 0);
    const now = Date.now();
    if (Number.isFinite(last) && last > 0 && now - last < WINDOW_MS) return false;
    window.sessionStorage.setItem(STORAGE_KEY, String(now));
  } catch {
    return false;
  }
  window.setTimeout(reload, RELOAD_DELAY_MS);
  return true;
}

/** Catches chunk failures that never reach an error boundary. */
export function installChunkReloadGuard(): () => void {
  if (typeof window === 'undefined') return () => {};
  const onError = (event: ErrorEvent) => {
    reloadOnceForChunkError(event.error || event.message);
  };
  const onRejection = (event: PromiseRejectionEvent) => {
    reloadOnceForChunkError(event.reason);
  };
  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);
  return () => {
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onRejection);
  };
}
